import {
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  LinkIcon,
} from '@heroicons/react/20/solid';
import { Loader2 } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { humanizeStatus,StatusPill } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type LeadDetail,
  useCreateIntakeDraftMutation,
  useGetLeadQuery,
  useUpdateLeadStatusMutation,
} from '@/services/api/leadIntegrationService';

const PROVIDER_LABELS: Record<string, string> = {
  lexamica: 'Lexamica',
  casexchange: 'CaseXChange',
};

function formatDate(iso?: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Field display
// ---------------------------------------------------------------------------

const Field = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div>
    <dt className="text-xs font-semibold text-gray-500 dark:text-zinc-400 uppercase tracking-wide">
      {label}
    </dt>
    <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
      {value || <span className="text-gray-500">—</span>}
    </dd>
  </div>
);

const Section = ({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="border-t border-gray-200 dark:border-zinc-700 pt-5">
    <h4 className="text-xs font-bold text-gray-700 dark:text-zinc-300 uppercase tracking-wider mb-4">
      {title}
    </h4>
    <dl className="grid grid-cols-2 gap-x-6 gap-y-4">{children}</dl>
  </div>
);

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

export const LeadDetailDrawer = ({
  leadId,
  open,
  onClose,
}: {
  leadId: string | null;
  open: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetLeadQuery(leadId ?? '', {
    skip: !leadId || !open,
  });
  const [updateStatus, { isLoading: isUpdating }] = useUpdateLeadStatusMutation();
  const [createDraft, { isLoading: isCreatingDraft }] = useCreateIntakeDraftMutation();

  const detail = data as LeadDetail | undefined;
  const lead = detail?.lead;
  const normalized = lead?.normalized_payload;
  const lexMeta = detail?.lexamica_metadata;
  const cxcMeta = detail?.casexchange_metadata;

  const canStartReview = lead?.status === 'new';
  const canCreateIntake =
    !lead?.intake_id && (lead?.status === 'new' || lead?.status === 'reviewing');

  // CaseXChange deep link — uses the referral ID, not base case ID
  const cxcUrl = cxcMeta?.referral_id
    ? `https://app.casexchange.com/casex/cases/${cxcMeta.referral_id}`
    : null;

  const handleStartReview = async () => {
    if (!lead) return;
    try {
      await updateStatus({ id: lead.id, status: 'reviewing' }).unwrap();
      toast.success('Lead set to evaluating');
    } catch {
      toast.error('Failed — outbound provider call may have failed');
    }
  };

  const handleCreateIntake = async () => {
    if (!lead) return;
    try {
      const result = await createDraft(lead.id).unwrap();
      const intakeId = result.lead.intake_id;
      if (intakeId) {
        toast.success('Intake draft created');
        navigate(`/intakes/${intakeId}`);
        onClose();
      }
    } catch {
      toast.error('Failed to create intake draft');
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-6">
        <SheetHeader className="pb-4 border-b border-gray-200 dark:border-zinc-700">
          <SheetTitle className="text-xl font-bold text-gray-900 dark:text-white">
            {normalized?.client_full_name || 'Lead Details'}
          </SheetTitle>
          <SheetDescription className="text-sm font-medium text-gray-600 dark:text-zinc-400 flex items-center gap-2">
            <span>
              {lead
                ? `${PROVIDER_LABELS[lead.provider] ?? lead.provider} · Received ${formatDate(lead.created_at)}`
                : ''}
            </span>
            {cxcUrl && (
              <a
                href={cxcUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <LinkIcon className="h-3 w-3" />
                View on CaseXChange
              </a>
            )}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : !lead ? (
          <div className="mt-8 text-center text-sm text-zinc-500">
            Lead not found.
          </div>
        ) : (
          <div className="space-y-5 mt-5">
            {/* Status + quick actions */}
            <div className="flex items-center gap-3 flex-wrap bg-gray-50 dark:bg-zinc-800/50 rounded-lg p-4">
              <StatusPill status={lead.status} />
              {lead.intake_status && (
                <StatusPill status={lead.intake_status} />
              )}
              {canStartReview && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleStartReview().catch(() => undefined)}
                  disabled={isUpdating}
                >
                  {isUpdating && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Start Review
                </Button>
              )}
              {canCreateIntake && (
                <Button
                  size="sm"
                  onClick={() => handleCreateIntake().catch(() => undefined)}
                  disabled={isCreatingDraft}
                >
                  {isCreatingDraft ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <DocumentTextIcon className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Create Intake
                </Button>
              )}
              {lead.intake_id && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigate(`/intakes/${lead.intake_id}`);
                    onClose();
                  }}
                >
                  <ArrowTopRightOnSquareIcon className="mr-1.5 h-3.5 w-3.5" />
                  View Intake
                </Button>
              )}
            </div>

            {/* Client info */}
            <Section title="Client">
              <Field label="Name" value={normalized?.client_full_name} />
              <Field label="Phone" value={normalized?.client_phone} />
              <Field label="Email" value={normalized?.client_email} />
              <Field label="Date of Birth" value={formatDate(normalized?.client_dob)} />
              <Field label="Address" value={normalized?.client_address} />
              {lead.live_transfer_requested && (
                <Field
                  label="Live Transfer"
                  value={
                    <span className="text-amber-600 font-medium">Requested</span>
                  }
                />
              )}
            </Section>

            {/* Incident info */}
            <Section title="Incident">
              <Field
                label="Date"
                value={formatDate(
                  normalized?.incident_date || lexMeta?.incident_date_raw,
                )}
              />
              <Field
                label="State"
                value={normalized?.incident_state || lexMeta?.incident_state}
              />
              <Field label="Location" value={normalized?.incident_location} />
              <Field
                label="Case Type"
                value={humanizeStatus(
                  normalized?.case_type || lexMeta?.case_type || cxcMeta?.case_type || '',
                )}
              />
              <Field label="Description" value={normalized?.incident_description} />
            </Section>

            {/* Referring firm */}
            {(lead.referring_firm_name || cxcMeta?.sending_firm_name) && (
              <Section title="Referring Firm">
                <Field
                  label="Firm Name"
                  value={lead.referring_firm_name || cxcMeta?.sending_firm_name}
                />
                {cxcMeta?.reference_number && (
                  <Field label="Reference #" value={cxcMeta.reference_number} />
                )}
              </Section>
            )}

            {/* CaseXChange-specific */}
            {cxcMeta && (
              <Section title={
                <span className="flex items-center gap-2">
                  CaseXChange Details
                  {cxcUrl && (
                    <a
                      href={cxcUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 normal-case tracking-normal"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                      Open
                    </a>
                  )}
                </span>
              }>
                <Field label="CXC Status" value={cxcMeta.cxc_status ? humanizeStatus(cxcMeta.cxc_status) : null} />
                <Field label="Jurisdiction" value={cxcMeta.jurisdiction} />
                <Field label="County" value={cxcMeta.county} />
                <Field label="Retainer Signed" value={cxcMeta.is_retainer_signed ? 'Yes' : 'No'} />
                {cxcMeta.lead_attorney_name && (
                  <Field label="Lead Attorney" value={cxcMeta.lead_attorney_name} />
                )}
                {cxcMeta.phase && <Field label="Phase" value={cxcMeta.phase} />}
                {cxcMeta.projected_settlement_amount != null && (
                  <Field
                    label="Projected Settlement"
                    value={`$${Number(cxcMeta.projected_settlement_amount).toLocaleString()}`}
                  />
                )}
                {cxcMeta.statute_of_limitations_expiration && (
                  <Field label="SOL Expiration" value={formatDate(cxcMeta.statute_of_limitations_expiration)} />
                )}
              </Section>
            )}

            {/* Missing fields warning */}
            {(() => {
              const missing = [
                !normalized?.client_full_name && 'Client name',
                !normalized?.client_phone && !normalized?.client_email && 'Phone or email',
                !normalized?.incident_date && 'Incident date',
                !normalized?.incident_state && 'Incident state',
                !(normalized?.case_type || lexMeta?.case_type || cxcMeta?.case_type) && 'Case type',
              ].filter(Boolean);

              if (missing.length === 0) return null;

              return (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-4">
                  <div className="text-xs font-bold text-amber-800 dark:text-amber-300 mb-1.5">
                    Missing fields
                  </div>
                  <div className="text-sm font-medium text-amber-700 dark:text-amber-400">
                    {missing.join(' · ')}
                  </div>
                </div>
              );
            })()}

            {/* Debug: raw payload (collapsed, subtle) */}
            <details className="rounded-md border border-gray-200 dark:border-zinc-700 mt-2">
              <summary className="cursor-pointer px-4 py-2.5 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-zinc-300">
                Developer: Raw payload
              </summary>
              <pre className="overflow-x-auto bg-gray-950 p-4 text-xs text-gray-300 rounded-b-md max-h-64 overflow-y-auto">
                {JSON.stringify(lead.raw_payload, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
